import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { ThemePalette } from '@angular/material/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import { TrackByFunctions } from '@principle-theorem/ng-shared';
import {
  Appointment,
  APPOINTMENT_STATUS_ACTION_LABEL_MAP,
  stafferToNamedDoc,
} from '@principle-theorem/principle-core';
import {
  AppointmentStatus,
  AppointmentSummary,
  IAppointment,
  IInvoice,
  InteractionType,
} from '@principle-theorem/principle-core/interfaces';
import {
  WithRef,
  asDocRef,
  filterUndefined,
  getDoc,
  saveDoc,
  shareReplayCold,
  snapshot,
} from '@principle-theorem/shared';
import { isString, startCase } from 'lodash';
import { Observable, ReplaySubject, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

interface IStatusWithLabel {
  status: AppointmentStatus;
  label: string | undefined;
}

@Component({
  selector: 'pr-appointment-status-workflow',
  templateUrl: './appointment-status-workflow.component.html',
  styleUrls: ['./appointment-status-workflow.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AppointmentStatusWorkflowComponent {
  appointment$ = new ReplaySubject<AppointmentSummary>(1);
  invoice$: Observable<WithRef<IInvoice> | undefined>;
  statuses = [
    AppointmentStatus.Scheduled,
    AppointmentStatus.Confirmed,
    AppointmentStatus.Arrived,
    AppointmentStatus.CheckedIn,
    AppointmentStatus.InProgress,
    AppointmentStatus.CheckingOut,
    AppointmentStatus.Complete,
  ];
  trackByStatus = TrackByFunctions.variable<AppointmentStatus>();
  currentStatus$ = this.appointment$.pipe(
    map((appointment) => ({
      status: appointment.metadata.status,
      label: startCase(appointment.metadata.status),
    }))
  );
  nextStatus$: Observable<IStatusWithLabel>;
  @Input() color: ThemePalette;
  @Input() type: 'stroked' | 'flat' = 'stroked';

  @Input()
  set appointment(appointment: AppointmentSummary) {
    if (appointment) {
      this.appointment$.next(appointment);
    }
  }

  constructor(
    private _snackBar: MatSnackBar,
    private _organisation: OrganisationService
  ) {
    this.nextStatus$ = this.currentStatus$.pipe(
      map((currentStatus) => {
        const currentIndex = this.statuses.indexOf(currentStatus.status);
        return this.buildStatusWithLabel(this.statuses[currentIndex + 1]);
      })
    );

    this.invoice$ = this.appointment$.pipe(
      switchMap((appointment) =>
        appointment.ref
          ? getDoc(asDocRef<IAppointment>(appointment.ref))
          : of(undefined)
      ),
      switchMap((appointment) =>
        appointment?.invoiceRef ? getDoc(appointment.invoiceRef) : of(undefined)
      ),
      shareReplayCold()
    );
  }

  isCurrentStatus$(status: AppointmentStatus): Observable<boolean> {
    return this.currentStatus$.pipe(
      map((currentStatus) => currentStatus.status === status)
    );
  }

  async updateStatus(status?: AppointmentStatus): Promise<void> {
    const staffer = await snapshot(
      this._organisation.staffer$.pipe(filterUndefined())
    );
    const appointmentSummary = await snapshot(this.appointment$);
    if (!status || !appointmentSummary.ref) {
      return;
    }
    const appointment = await getDoc(
      asDocRef<IAppointment>(appointmentSummary.ref)
    );
    await Appointment.updateStatus(
      appointment,
      status,
      InteractionType.Appointment,
      stafferToNamedDoc(staffer)
    );
    await saveDoc(appointment);
    this._snackBar.open('Status updated');
  }

  buildStatusWithLabel(status: AppointmentStatus): IStatusWithLabel {
    return {
      status,
      label:
        APPOINTMENT_STATUS_ACTION_LABEL_MAP[status]?.nextLabel ??
        startCase(status),
    };
  }

  buildStatusTooltip$(data: {
    status: AppointmentStatus;
    index: number;
  }): Observable<string | undefined> {
    return combineLatest([
      this.currentStatus$,
      this.appointment$,
      this.invoice$,
    ]).pipe(
      map(([currentStatus, appointment, invoice]) => {
        if (currentStatus.status === data.status) {
          return;
        }
        const currentStatusIndex = this.statuses.indexOf(currentStatus.status);

        if (currentStatusIndex > data.index) {
          const canRevert = APPOINTMENT_STATUS_ACTION_LABEL_MAP[
            data.status
          ]?.canRevertTo(appointment, invoice);

          return isString(canRevert) ? canRevert : undefined;
        }
        const canGoNext =
          APPOINTMENT_STATUS_ACTION_LABEL_MAP[data.status]?.canGoNext(
            appointment
          );
        return isString(canGoNext) ? canGoNext : undefined;
      })
    );
  }

  isDisabled$(data: {
    status: AppointmentStatus;
    index: number;
  }): Observable<boolean> {
    return combineLatest([
      this.currentStatus$,
      this.appointment$,
      this.invoice$,
    ]).pipe(
      map(([currentStatus, appointment, invoice]) => {
        if (currentStatus.status === data.status) {
          return true;
        }
        const currentStatusIndex = this.statuses.indexOf(currentStatus.status);

        if (currentStatusIndex > data.index) {
          const canRevert = APPOINTMENT_STATUS_ACTION_LABEL_MAP[
            data.status
          ]?.canRevertTo(appointment, invoice);

          return isString(canRevert) ? true : !canRevert;
        }
        const canGoNext =
          APPOINTMENT_STATUS_ACTION_LABEL_MAP[data.status]?.canGoNext(
            appointment
          );
        return isString(canGoNext) ? true : !canGoNext;
      })
    );
  }
}
